import { useState, useEffect } from 'react';
import { Menu, X, ExternalLink } from 'lucide-react';
import { getAnalyzerUrl } from '../../config/api';
import CrawlerVisionLogo from '../CrawlerVisionLogo';

const navLinks = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Contact', href: '#contact' },
];

const HomeHeader = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (e, href) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    }
  };

  return (
    <header
      className={`cv-customer-topnav cv-customer-topnav-lift sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? 'shadow-lg backdrop-blur-md' : ''
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity" title="Crawler Vision">
            <CrawlerVisionLogo
              variant="onDark"
              className="h-12 w-auto max-w-[320px] object-contain object-left sm:h-14 sm:max-w-[380px]"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="px-3.5 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/15 rounded-lg transition-all"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/partner"
              className="px-3.5 py-2 text-sm font-semibold text-white bg-white/15 hover:bg-white/25 rounded-lg transition-all border border-white/30"
            >
              Partner
            </a>
            <a
              href="/dashboard"
              title="Open your Analytics Dashboard"
              className="ml-1 px-3.5 py-2 text-sm font-semibold text-white bg-cv-sky/25 hover:bg-cv-sky/45 rounded-lg transition-all border border-cv-sky/55 flex items-center gap-1.5"
            >
              <span aria-hidden="true" className="text-base leading-none">📊</span>
              Dashboard
            </a>
            <a
              href={getAnalyzerUrl()}
              className="cv-customer-cta cv-emboss ml-1 px-4 py-2 font-semibold text-sm rounded-lg transition-all flex items-center gap-1.5"
            >
              Launch Analyzer <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </nav>

          {/* Mobile toggle */}
          <button
            className="md:hidden p-2 text-white hover:bg-white/15 rounded-lg transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/20">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block px-3 py-2.5 text-sm font-medium text-white/90 hover:text-white hover:bg-white/15 rounded-lg transition-all"
              >
                {link.label}
              </a>
            ))}
            <a
              href="/partner"
              className="block px-3 py-2.5 text-sm font-semibold text-white bg-white/15 rounded-lg transition-all border border-white/30"
            >
              Partner
            </a>
            <a
              href="/dashboard"
              className="mt-1 px-3 py-2.5 text-sm font-semibold text-white bg-cv-sky/25 hover:bg-cv-sky/45 rounded-lg transition-all border border-cv-sky/55 flex items-center justify-center gap-1.5"
            >
              <span aria-hidden="true" className="text-base leading-none">📊</span>
              Dashboard
            </a>
            <a
              href={getAnalyzerUrl()}
              className="cv-customer-cta cv-emboss block mt-2 px-3 py-2.5 font-semibold text-sm rounded-lg text-center transition-all"
            >
              Launch Analyzer
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default HomeHeader;
